import { type Product } from "~/api/products/types";
import { type BasketStore } from "~/providers/store/basket";
import { type BasketItem } from "~/stores/kiosk-session/types";

type InferStockStatusFromBasketArgs = {
  product: Pick<Product, "id" | "stock">;
  basket: BasketItem[] | BasketStore["products"];
};

/**
 * The remaining stock for a product.
 * - `out-of-stock` if the product is out of stock
 * - `reserved` if the product has a reserved stock (reached limit in the basket)
 * - `unlimited` if the product has unlimited stock
 * - `number` remaining stock
 */
export type StockStatus = number | "reserved" | "out-of-stock" | "unlimited";

export function inferStockStatusFromBasket({
  product,
  basket
}: InferStockStatusFromBasketArgs): StockStatus {
  if (product.stock === null) {
    return "unlimited";
  }

  if (product.stock <= 0) {
    return "out-of-stock";
  }

  const quantityInBasket = basket.reduce((acc, item) => {
    if (item.productId === product.id) {
      return acc + ("count" in item ? item.count : item.quantity);
    }

    return acc;
  }, 0);

  return inferStockStatus({
    stock: product.stock,
    quantity: quantityInBasket
  });
}

type InferStockStatusArgs = {
  stock: number | null | StockStatus;
  quantity: number;
};

export function inferStockStatus({
  stock,
  quantity
}: InferStockStatusArgs): StockStatus {
  if (stock === null) {
    return "unlimited";
  }

  if (typeof stock === "string") {
    return stock;
  }

  if (stock <= 0) {
    return "out-of-stock";
  }

  if (quantity >= stock) {
    return "reserved";
  }

  return stock - quantity;
}
